import {
  OPTIONAL_CONSENT_CATEGORIES,
} from './constants.js';
import type { OptionalConsentCategory } from './constants.js';
import type { ConsentClient } from './types.js';

const SUPPORTED_SCRIPT_TYPES = new Set([
  'text/javascript',
  'application/javascript',
  'module',
]);
const SUPPORTED_REFERRER_POLICIES = new Set([
  'no-referrer',
  'no-referrer-when-downgrade',
  'origin',
  'origin-when-cross-origin',
  'same-origin',
  'strict-origin',
  'strict-origin-when-cross-origin',
  'unsafe-url',
]);

export type ConsentBlockerViolationCode =
  | 'activation_failed'
  | 'ambiguous_script_source'
  | 'invalid_category'
  | 'invalid_source_url'
  | 'missing_source'
  | 'unsafe_source_present'
  | 'unsupported_element'
  | 'unsupported_script_type';

export interface ConsentBlockerViolation {
  readonly code: ConsentBlockerViolationCode;
  readonly element: Element;
  readonly category?: string;
}

export interface ConsentBlockerOptions {
  readonly client: Pick<ConsentClient, 'has' | 'on'>;
  readonly document?: Document;
  readonly mutationObserver?: typeof MutationObserver;
  readonly allowInsecureLocalhost?: boolean;
  readonly onViolation?: (violation: ConsentBlockerViolation) => void;
}

export interface ConsentBlocker {
  start(): void;
  stop(): void;
  refresh(): void;
  readonly started: boolean;
}

export type ConsentBlockerErrorCode =
  | 'document_unavailable'
  | 'mutation_observer_unavailable';

export class ConsentBlockerError extends Error {
  readonly code: ConsentBlockerErrorCode;

  constructor(code: ConsentBlockerErrorCode, message: string) {
    super(message);
    this.name = 'ConsentBlockerError';
    this.code = code;
  }
}

interface ActiveIframe {
  readonly category: OptionalConsentCategory;
  readonly placeholder: HTMLIFrameElement;
  readonly active: HTMLIFrameElement;
}

function isOptionalCategory(value: string): value is OptionalConsentCategory {
  return (OPTIONAL_CONSENT_CATEGORIES as readonly string[]).includes(value);
}

function isLocalhost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]'
  );
}

function parseSafeSourceUrl(
  rawValue: string,
  baseUrl: string,
  allowInsecureLocalhost: boolean,
): URL | null {
  let url: URL;
  try {
    url = new URL(rawValue, baseUrl);
  } catch {
    return null;
  }

  if (url.username !== '' || url.password !== '') return null;
  if (url.protocol === 'https:') return url;
  if (
    url.protocol === 'http:' &&
    allowInsecureLocalhost &&
    isLocalhost(url.hostname)
  ) {
    return url;
  }
  return null;
}

function copyReferrerPolicy(source: Element, target: Element): void {
  const value = source.getAttribute('referrerpolicy');
  if (value && SUPPORTED_REFERRER_POLICIES.has(value.toLowerCase())) {
    target.setAttribute('referrerpolicy', value.toLowerCase());
  }
}

function copyScriptAttributes(
  source: HTMLScriptElement,
  target: HTMLScriptElement,
): void {
  if (source.nonce) target.nonce = source.nonce;
  if (source.integrity) target.integrity = source.integrity;
  if (source.crossOrigin === 'anonymous' || source.crossOrigin === 'use-credentials') {
    target.crossOrigin = source.crossOrigin;
  }
  target.async = source.hasAttribute('async');
  if (source.hasAttribute('defer')) target.defer = true;
  if (source.hasAttribute('nomodule')) target.noModule = true;

  const fetchPriority = source.getAttribute('fetchpriority');
  if (fetchPriority && ['high', 'low', 'auto'].includes(fetchPriority)) {
    target.setAttribute('fetchpriority', fetchPriority);
  }
  copyReferrerPolicy(source, target);
}

function copyIframeAttributes(
  source: HTMLIFrameElement,
  target: HTMLIFrameElement,
): void {
  for (const attribute of [
    'allow',
    'class',
    'height',
    'loading',
    'name',
    'sandbox',
    'style',
    'title',
    'width',
  ]) {
    const value = source.getAttribute(attribute);
    if (value !== null) target.setAttribute(attribute, value);
  }
  if (source.hasAttribute('allowfullscreen')) {
    target.setAttribute('allowfullscreen', '');
  }
  copyReferrerPolicy(source, target);
  if (!target.hasAttribute('referrerpolicy')) {
    target.setAttribute('referrerpolicy', 'no-referrer');
  }
}

export function createConsentBlocker(
  options: ConsentBlockerOptions,
): ConsentBlocker {
  let blockerStarted = false;
  let documentReference: Document | null = null;
  let observer: MutationObserver | null = null;
  let unsubscribe: (() => void) | null = null;

  const executedScripts = new WeakSet<HTMLScriptElement>();
  const activeIframes = new Set<ActiveIframe>();
  const reportedViolations = new WeakMap<Element, Set<ConsentBlockerViolationCode>>();

  function report(
    element: Element,
    code: ConsentBlockerViolationCode,
    category?: string,
  ): void {
    let reported = reportedViolations.get(element);
    if (!reported) {
      reported = new Set();
      reportedViolations.set(element, reported);
    }
    if (reported.has(code)) return;
    reported.add(code);

    if (!options.onViolation) return;
    try {
      options.onViolation({
        code,
        element,
        ...(category === undefined ? {} : { category }),
      });
    } catch {
      // Diagnostics must not change blocking behavior.
    }
  }

  function setStatus(element: Element, status: 'blocked' | 'error' | 'loaded'): void {
    element.setAttribute('data-goksima-consent-status', status);
  }

  function activateScript(
    placeholder: HTMLScriptElement,
    category: OptionalConsentCategory,
  ): void {
    if (executedScripts.has(placeholder)) {
      setStatus(placeholder, 'loaded');
      return;
    }
    if (placeholder.hasAttribute('src')) {
      setStatus(placeholder, 'error');
      report(placeholder, 'unsafe_source_present', category);
      return;
    }

    const rawSource = placeholder.getAttribute('data-src')?.trim() ?? '';
    const inlineSource = placeholder.textContent ?? '';
    if (rawSource && inlineSource.trim()) {
      setStatus(placeholder, 'error');
      report(placeholder, 'ambiguous_script_source', category);
      return;
    }
    if (!rawSource && !inlineSource.trim()) {
      setStatus(placeholder, 'error');
      report(placeholder, 'missing_source', category);
      return;
    }

    const scriptType = placeholder.getAttribute('data-type') ?? 'text/javascript';
    if (!SUPPORTED_SCRIPT_TYPES.has(scriptType)) {
      setStatus(placeholder, 'error');
      report(placeholder, 'unsupported_script_type', category);
      return;
    }

    const active = documentReference?.createElement('script');
    if (!active || !placeholder.parentNode) {
      setStatus(placeholder, 'error');
      report(placeholder, 'activation_failed', category);
      return;
    }

    active.type = scriptType;
    active.setAttribute('data-goksima-consent-activated', category);
    copyScriptAttributes(placeholder, active);

    if (rawSource) {
      const url = parseSafeSourceUrl(
        rawSource,
        placeholder.ownerDocument.baseURI,
        options.allowInsecureLocalhost ?? false,
      );
      if (!url) {
        setStatus(placeholder, 'error');
        report(placeholder, 'invalid_source_url', category);
        return;
      }
      active.src = url.href;
    } else {
      active.textContent = inlineSource;
    }

    try {
      placeholder.parentNode.insertBefore(active, placeholder.nextSibling);
      executedScripts.add(placeholder);
      setStatus(placeholder, 'loaded');
    } catch {
      setStatus(placeholder, 'error');
      report(placeholder, 'activation_failed', category);
    }
  }

  function activateIframe(
    placeholder: HTMLIFrameElement,
    category: OptionalConsentCategory,
  ): void {
    if (placeholder.hasAttribute('src')) {
      setStatus(placeholder, 'error');
      report(placeholder, 'unsafe_source_present', category);
      return;
    }

    const rawSource = placeholder.getAttribute('data-src')?.trim() ?? '';
    if (!rawSource) {
      setStatus(placeholder, 'error');
      report(placeholder, 'missing_source', category);
      return;
    }

    const url = parseSafeSourceUrl(
      rawSource,
      documentReference?.baseURI ?? '',
      options.allowInsecureLocalhost ?? false,
    );
    if (!url || !documentReference || !placeholder.parentNode) {
      setStatus(placeholder, 'error');
      report(
        placeholder,
        url ? 'activation_failed' : 'invalid_source_url',
        category,
      );
      return;
    }

    const active = documentReference.createElement('iframe');
    active.src = url.href;
    active.setAttribute('data-goksima-consent-activated', category);
    copyIframeAttributes(placeholder, active);

    try {
      placeholder.replaceWith(active);
      activeIframes.add({ category, placeholder, active });
    } catch {
      setStatus(placeholder, 'error');
      report(placeholder, 'activation_failed', category);
    }
  }

  function processElement(element: Element): void {
    const categoryValue = element.getAttribute('data-consent') ?? '';
    if (!isOptionalCategory(categoryValue)) {
      setStatus(element, 'error');
      report(element, 'invalid_category', categoryValue);
      return;
    }

    if (!options.client.has(categoryValue)) {
      setStatus(element, 'blocked');
      return;
    }

    if (element.localName === 'script') {
      activateScript(element as HTMLScriptElement, categoryValue);
    } else if (element.localName === 'iframe') {
      activateIframe(element as HTMLIFrameElement, categoryValue);
    } else {
      setStatus(element, 'error');
      report(element, 'unsupported_element', categoryValue);
    }
  }

  function processRoot(root: Node): void {
    const element = root.nodeType === 1 ? (root as Element) : null;
    if (element?.hasAttribute('data-consent')) {
      processElement(element);
    }
    const queryRoot = root as Node & Partial<ParentNode>;
    if (typeof queryRoot.querySelectorAll === 'function') {
      for (const candidate of queryRoot.querySelectorAll('[data-consent]')) {
        processElement(candidate);
      }
    }
  }

  function removeRevokedIframes(): void {
    for (const record of [...activeIframes]) {
      if (!record.active.isConnected) {
        activeIframes.delete(record);
        continue;
      }
      if (options.client.has(record.category)) continue;

      record.active.replaceWith(record.placeholder);
      setStatus(record.placeholder, 'blocked');
      activeIframes.delete(record);
    }
  }

  const blocker: ConsentBlocker = {
    start(): void {
      if (blockerStarted) return;

      documentReference = options.document ?? globalThis.document ?? null;
      if (!documentReference) {
        throw new ConsentBlockerError(
          'document_unavailable',
          'Consent blocker requires a browser document.',
        );
      }

      const Observer = options.mutationObserver ?? globalThis.MutationObserver;
      if (!Observer) {
        throw new ConsentBlockerError(
          'mutation_observer_unavailable',
          'Consent blocker requires MutationObserver for dynamic DOM safety.',
        );
      }

      blockerStarted = true;
      unsubscribe = options.client.on('status-changed', () => blocker.refresh());
      observer = new Observer((records) => {
        for (const record of records) {
          for (const node of record.addedNodes) processRoot(node);
        }
      });
      observer.observe(documentReference, { childList: true, subtree: true });
      blocker.refresh();
    },

    stop(): void {
      if (!blockerStarted) return;
      observer?.disconnect();
      observer = null;
      unsubscribe?.();
      unsubscribe = null;
      blockerStarted = false;
    },

    refresh(): void {
      if (!blockerStarted || !documentReference) return;
      removeRevokedIframes();
      processRoot(documentReference);
    },

    get started(): boolean {
      return blockerStarted;
    },
  };

  return Object.freeze(blocker);
}
